import { useState, type ReactNode } from 'react'
import { useAuth } from '../lib/auth'

// In-app help. Content mirrors the shared how-to guide, but is scoped to what
// the signed-in person can actually do and shows their real form link.
export function HelpPage() {
  const { user, company } = useAuth()
  const isAdmin = user?.role === 'company_admin' || user?.role === 'super_admin'
  const isSuper = user?.role === 'super_admin'
  const formUrl = company ? `${window.location.origin}/f/${company.form_token}` : ''

  return (
    <div style={{ maxWidth: 780 }}>
      <div className="page-title">Help &amp; how-to</div>
      <p className="page-sub">
        Short answers for everyday tasks. Open a section to see the steps.
      </p>

      {formUrl && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>Your damage-report form link</div>
          <p className="page-sub" style={{ marginBottom: 10 }}>
            Send this to your field technicians — they don't need a login.
          </p>
          <CopyRow url={formUrl} />
        </div>
      )}

      <Topic title="How technicians report damage" defaultOpen>
        <p>Technicians open your form link on their phone — no account needed.</p>
        <ol className="help-steps">
          <li><b>Open the link</b> (bookmark it for one-tap access).</li>
          <li><b>Add photos</b> — tap “+ Add photos” to take one or pick from the library. Add several; remove any with the ✕. At least one is required.</li>
          <li><b>Fill in details</b> — pick their name from the technician list (or type it), customer name and phone, address, and a description. The date defaults to now.</li>
          <li><b>Set severity</b> — Minor, Moderate, or Major.</li>
          <li><b>Submit</b> — they get a case number like <code>CAS-000123</code>, and it appears in Open Damages instantly.</li>
        </ol>
        <p className="help-tip">
          <b>Tip:</b> Give each technician a <b>personal link</b> (Settings → Technicians → Copy link). It fills in their name automatically, so every report is credited correctly.
        </p>
      </Topic>

      <Topic title="Reading the dashboard">
        <p>
          Filter by <b>Week</b>, <b>Month</b>, or <b>Year</b> to see total damages, counts by status,
          total repair cost, damages over time, your top technicians and most-used subcontractors,
          and recent activity.
        </p>
        <p className="help-tip">
          <b>Tip:</b> Click any technician or subcontractor name on the dashboard to open their
          profile — contact info, stats, and every case they worked.
        </p>
      </Topic>

      <Topic title="Working a case">
        <p>Open the <b>Open</b>, <b>In Progress</b>, or <b>Closed</b> tab and click a case.</p>
        <ol className="help-steps">
          <li><b>Review it</b> — customer info, photos, description, severity. Tap the phone number to call or the address to open maps.</li>
          <li><b>Assign a subcontractor</b> from the dropdown; their contact shows underneath.</li>
          <li><b>Log costs</b> — upload an invoice, receipt, or cost file with an amount. The running total stays in view.</li>
          <li><b>Add comments</b> to record what's happening.</li>
          <li><b>Move the status</b> Open → In Progress → Closed. Closing stamps the completion date. Every change is logged to the activity feed.</li>
        </ol>
        {isAdmin && (
          <p className="help-tip">
            <b>Admins:</b> remove a wrong photo with the ✕ on it, or a document with its
            <b> Delete</b> link.
          </p>
        )}
      </Topic>

      <Topic title="Finding a case">
        <p>
          Use <b>Search</b> to look up a case by number or keyword — customer name, address,
          technician, or phone — and narrow it by status and a date range. Click any result to open it.
        </p>
      </Topic>

      <Topic title="Subcontractors & technicians">
        <p>
          <b>Subcontractors</b> lists the subs you assign to cases — add, edit, or remove them, and
          click a name for their profile and case history.
        </p>
        {isAdmin ? (
          <p>
            <b>Technicians</b> live under Settings. Add your field crew there, then copy each
            technician's personal form link. Their names are clickable for a profile and history.
          </p>
        ) : (
          <p>Technicians are managed by your company administrator under Settings.</p>
        )}
      </Topic>

      {isAdmin && (
        <Topic title="Team, sharing & importing (admins)">
          <ul className="help-list">
            <li><b>Team members</b> — add users and set each as <b>Admin</b> or <b>User</b>. Toggle who gets an email when a new damage is reported.</li>
            <li><b>Share the form</b> — copy your public form link and send it to your crews.</li>
            <li><b>Import history</b> — upload an existing damage log (.xlsx or .csv). Columns are matched automatically; review the preview, then import.</li>
          </ul>
        </Topic>
      )}

      {isSuper && (
        <Topic title="Managing companies (Resilience admins)">
          <ol className="help-steps">
            <li><b>New Company</b> — creates the company and its first admin, who can then invite their own team.</li>
            <li><b>Open</b> — enter any company's workspace, scoped to them. A banner shows which company you're viewing.</li>
            <li><b>← Back to admin</b> — leave that company and pick another.</li>
          </ol>
        </Topic>
      )}

      <Topic title="Roles & permissions">
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Capability</th>
                <th>User</th>
                <th>Company admin</th>
                <th>Resilience admin</th>
              </tr>
            </thead>
            <tbody>
              <tr><td>View dashboard &amp; cases</td><td>✓</td><td>✓</td><td>✓</td></tr>
              <tr><td>Assign subs, costs, comments, status</td><td>✓</td><td>✓</td><td>✓</td></tr>
              <tr><td>Delete photos &amp; documents</td><td>—</td><td>✓</td><td>✓</td></tr>
              <tr><td>Manage team, technicians, import</td><td>—</td><td>✓</td><td>✓</td></tr>
              <tr><td>Provision &amp; switch companies</td><td>—</td><td>—</td><td>✓</td></tr>
            </tbody>
          </table>
        </div>
        <p className="help-tip" style={{ marginTop: 12 }}>
          Field technicians never log in — they only use the form link.
        </p>
      </Topic>

      <Topic title="Status & severity">
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
          <span className="badge" style={{ background: 'var(--status-open)' }}>Open</span>
          <span style={{ fontSize: 14, color: 'var(--gray)' }}>reported, not started ·</span>
          <span className="badge" style={{ background: 'var(--status-in-progress)', color: 'var(--ink)' }}>In Progress</span>
          <span style={{ fontSize: 14, color: 'var(--gray)' }}>being worked ·</span>
          <span className="badge" style={{ background: 'var(--status-closed)' }}>Closed</span>
          <span style={{ fontSize: 14, color: 'var(--gray)' }}>resolved &amp; dated</span>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          <span className="badge" style={{ background: 'var(--sev-minor)' }}>Minor</span>
          <span className="badge" style={{ background: 'var(--sev-moderate)', color: 'var(--ink)' }}>Moderate</span>
          <span className="badge" style={{ background: 'var(--sev-major)' }}>Major</span>
          <span style={{ fontSize: 14, color: 'var(--gray)' }}>— how urgent the damage is</span>
        </div>
        <p className="help-tip" style={{ marginTop: 12 }}>
          Case numbers like <code>CAS-000123</code> are assigned automatically. Everything saves as
          you go — there's no separate save button.
        </p>
      </Topic>

      <Topic title="Your account">
        <p>
          Change your password under <b>Settings → Change your password</b>
          {isSuper ? ' (or on the admin portal)' : ''}. Changing it signs out your other sessions.
        </p>
      </Topic>
    </div>
  )
}

function Topic({
  title,
  children,
  defaultOpen,
}: {
  title: string
  children: ReactNode
  defaultOpen?: boolean
}) {
  return (
    <details className="help-topic" open={defaultOpen}>
      <summary>{title}</summary>
      <div className="help-body">{children}</div>
    </details>
  )
}

function CopyRow({ url }: { url: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <div style={{ display: 'flex', gap: 8 }}>
      <input
        readOnly
        value={url}
        onFocus={(e) => e.target.select()}
        style={{
          flex: 1,
          padding: '9px 12px',
          border: '1px solid var(--line)',
          borderRadius: 'var(--radius-sm)',
          font: 'inherit',
          background: 'var(--bg)',
        }}
      />
      <button
        className="btn btn-primary"
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(url)
            setCopied(true)
            setTimeout(() => setCopied(false), 1500)
          } catch {
            /* clipboard blocked; the field is selectable */
          }
        }}
      >
        {copied ? 'Copied!' : 'Copy'}
      </button>
    </div>
  )
}
